import * as RadixTabs from '@radix-ui/react-tabs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cn, useResponsiveBreakPoints } from '@restworld/utils-common';

export type TabItem = {
  /** Tab label text or custom ReactNode */
  label: string | React.ReactNode;
  /** Tab panel content */
  content: React.ReactNode;
  /** Whether this tab is disabled */
  disabled?: boolean;
  /** Icon element rendered before the label */
  icon?: React.ReactNode;
  /** Badge content rendered after the label */
  badge?: string | number | React.ReactNode;
};

export type TabsProps = {
  /** Array of tab items to render */
  tabs: TabItem[];
  /** Additional class name for the root wrapper */
  className?: string;
  /** Additional class name for the tab list container */
  tabListClassName?: string;
  /** Additional class name for each tab panel */
  tabPanelClassName?: string;
  /** Callback fired when the active tab changes */
  onTabChange?: (index: number) => void;
  /** Index of the initially selected tab */
  defaultIndex?: number;
  /** Whether the tab list should stick to the top on scroll */
  sticky?: boolean;
  /** Top offset in pixels when sticky (desktop). Defaults to 88. */
  stickyOffset?: number;
  /** Top offset in pixels when sticky on mobile. Defaults to 74. */
  mobileStickyOffset?: number;
  /** Whether to center tabs horizontally */
  centerTabs?: boolean;
};

/**
 * Tabs component built on Radix UI Tabs.
 *
 * Design matches dev.employer.restworld.it (master branch):
 * - Active tab: white bg, purple border + shadow, black text
 * - Inactive tab: white bg, light gray border, black text
 * - Both: pill-shaped with rounded-lg, centered icons + label + badge
 */
export const Tabs = ({
  tabs,
  className,
  tabListClassName,
  tabPanelClassName,
  onTabChange,
  defaultIndex = 0,
  sticky = false,
  stickyOffset = 88,
  mobileStickyOffset = 74,
  centerTabs = false,
}: TabsProps) => {
  const [activeTab, setActiveTab] = useState(String(defaultIndex));
  const tabListRef = useRef<HTMLDivElement>(null);
  const { isDesktop } = useResponsiveBreakPoints();
  const topOffset = isDesktop ? stickyOffset : mobileStickyOffset;

  useEffect(() => {
    if (defaultIndex !== undefined) {
      setActiveTab(String(defaultIndex));
    }
  }, [defaultIndex]);

  useEffect(() => {
    if (tabListRef.current) {
      const activeTabElement = tabListRef.current.querySelector('[data-state="active"]');
      if (activeTabElement) {
        activeTabElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [activeTab]);

  const handleTabChange = useCallback(
    (value: string) => {
      const index = Number(value);
      setActiveTab(value);
      onTabChange?.(index);
    },
    [onTabChange]
  );

  return (
    <RadixTabs.Root value={activeTab} onValueChange={handleTabChange} className="w-full">
      <RadixTabs.List
        ref={tabListRef}
        className={cn(
          'flex gap-3 mb-4 px-1 pt-6 pb-1 max-w-full overflow-x-auto scrollbar-none',
          centerTabs && 'justify-center',
          sticky && 'sticky z-30 left-0 right-0 pt-4 pb-6 bg-neutral-10',
          tabListClassName
        )}
        style={sticky ? { top: `${topOffset}px` } : undefined}
      >
        {tabs.map((tab, index) => {
          const isActive = activeTab === String(index);
          return (
            <TabTrigger key={index} value={String(index)} disabled={tab.disabled} isActive={isActive}>
              {tab.icon && <TabIcon>{tab.icon}</TabIcon>}
              <span>{tab.label}</span>
              {tab.badge !== undefined && tab.badge !== null && <TabBadge>{tab.badge}</TabBadge>}
            </TabTrigger>
          );
        })}
      </RadixTabs.List>

      <div className={cn('w-full', className)}>
        {tabs.map(({ content }, index) => (
          <RadixTabs.Content key={index} value={String(index)} className={cn('mt-6 p-0', tabPanelClassName)}>
            {content}
          </RadixTabs.Content>
        ))}
      </div>
    </RadixTabs.Root>
  );
};

const TabTrigger = ({
  isActive,
  className,
  children,
  ...props
}: { isActive: boolean } & React.ComponentProps<typeof RadixTabs.Trigger>) => (
  <RadixTabs.Trigger
    className={cn(
      'flex items-center gap-2 rounded-lg px-5 py-3 text-sm whitespace-nowrap font-medium transition-all cursor-pointer',
      'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-2',
      'bg-white border text-black',
      isActive
        ? 'border-primary-700 shadow-[0_0_0_1px_rgba(105,62,181,0.3)]'
        : 'border-neutral-40 hover:border-neutral-90 hover:bg-neutral-20',
      props.disabled && 'opacity-50 cursor-not-allowed',
      className
    )}
    {...props}
  >
    {children}
  </RadixTabs.Trigger>
);

const TabIcon = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('flex items-center text-black [&_svg]:w-5 [&_svg]:h-5', className)} {...props} />
);

const TabBadge = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span
    className={cn(
      'flex items-center justify-center w-6 h-6 min-w-[24px] min-h-[24px]',
      'rounded-full text-xs font-semibold text-primary-700 bg-secondary',
      className
    )}
    {...props}
  />
);
